import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ComplianceModeProvider } from "./contexts/ComplianceModeContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import AssetsList from "./pages/assets/AssetsList";
import AssetDetail from "./pages/assets/AssetDetail";
import AssetForm from "./pages/assets/AssetForm";
import AssetImport from "./pages/assets/AssetImport";
import ItamImport from "./pages/assets/ItamImport";
import AssetDependencies from "./pages/assets/AssetDependencies";
import Requirements from "./pages/Requirements";
import Tasks from "./pages/Tasks";
import Evidence from "./pages/Evidence";
import Incidents from "./pages/Incidents";
import Risks from "./pages/Risks";
import Vulnerabilities from "./pages/Vulnerabilities";
import Reports from "./pages/Reports";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import UsersManagement from "./pages/settings/UsersManagement";
import LeadsDashboard from "./pages/settings/LeadsDashboard";
import Approvals from "./pages/Approvals";
// EU AI Act
import AiActDashboard from "./pages/ai-act/AiActDashboard";
import AiSystemsList from "./pages/ai-act/AiSystemsList";
import AiSystemDetail from "./pages/ai-act/AiSystemDetail";
import AiClassifyWizard from "./pages/ai-act/AiClassifyWizard";
import AiRisks from "./pages/ai-act/AiRisks";
import AiIncidents from "./pages/ai-act/AiIncidents";
import GpaiModels from "./pages/ai-act/GpaiModels";
import AiAuditTrail from "./pages/ai-act/AiAuditTrail";
import AiPostMarket from "./pages/ai-act/AiPostMarket";
import AiActRequirements from "./pages/ai-act/AiActRequirements";
import ComplianceCalendar from "./pages/ComplianceCalendar";
import BoardGovernance from "./pages/BoardGovernance";
import Training from "./pages/Training";
import Suppliers from "./pages/Suppliers";
import Policies from "./pages/Policies";
import Bcm from "./pages/Bcm";
import Audits from "./pages/Audits";
import Dpo from "./pages/Dpo";
import Documents from "./pages/Documents";
// Public landing
import AiActLanding from "./pages/landing/AiActLanding";
import Nis2Landing from "./pages/landing/Nis2Landing";
import TrustCenter from "./pages/landing/TrustCenter";
import VerifyBadge from "./pages/landing/VerifyBadge";
import VerifyCertificate from "./pages/landing/VerifyCertificate";
import PublicPortfolio from "./pages/landing/PublicPortfolio";
import InsightsList from "./pages/landing/InsightsList";
import InsightDetail from "./pages/landing/InsightDetail";
import InsightsAdmin from "./pages/settings/InsightsAdmin";
import GrowthDashboard from "./pages/settings/GrowthDashboard";
import PageviewBeacon from "./hooks/usePageviewBeacon";

// Super Admin (cross-tenant)
import SuperAdminLogin from "./pages/super-admin/SuperAdminLogin";
import SuperAdminLayout from "./pages/super-admin/SuperAdminLayout";
import SuperAdminDashboard from "./pages/super-admin/SuperAdminDashboard";
import SuperAdminTenants from "./pages/super-admin/SuperAdminTenants";
import SuperAdminInsights from "./pages/super-admin/SuperAdminInsights";
import SuperAdminAuditLog from "./pages/super-admin/SuperAdminAuditLog";
import SuperAdminBranding from "./pages/super-admin/SuperAdminBranding";
import SuperAdminCustomTemplates from "./pages/super-admin/SuperAdminCustomTemplates";
import BrandingProvider from "./components/BrandingProvider";

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This must be checked synchronously during render, not in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <PageviewBeacon />
      <BrandingProvider />
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/landing/ai-act" element={<AiActLanding />} />
      <Route path="/landing/nis2" element={<Nis2Landing />} />
      <Route path="/trust" element={<TrustCenter />} />
      <Route path="/verify/certificate/:certId" element={<VerifyCertificate />} />
      <Route path="/perfil/:userId" element={<PublicPortfolio />} />
      <Route path="/verify/:leadId" element={<VerifyBadge />} />
      <Route path="/insights" element={<InsightsList />} />
      <Route path="/insights/:slug" element={<InsightDetail />} />

      {/* Super Admin (cross-tenant) — separate auth stack */}
      <Route path="/super-admin/login" element={<SuperAdminLogin />} />
      <Route path="/super-admin" element={<SuperAdminLayout />}>
        <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
        <Route path="dashboard" element={<SuperAdminDashboard />} />
        <Route path="tenants" element={<SuperAdminTenants />} />
        <Route path="tenants/:tenantId/branding" element={<SuperAdminBranding />} />
        <Route path="tenants/:tenantId/templates" element={<SuperAdminCustomTemplates />} />
        <Route path="insights" element={<SuperAdminInsights />} />
        <Route path="audit-log" element={<SuperAdminAuditLog />} />
      </Route>
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      {/* Assets */}
      <Route path="/assets" element={
        <ProtectedRoute>
          <AssetsList />
        </ProtectedRoute>
      } />
      <Route path="/assets/new" element={
        <ProtectedRoute>
          <AssetForm />
        </ProtectedRoute>
      } />
      <Route path="/assets/import" element={
        <ProtectedRoute>
          <AssetImport />
        </ProtectedRoute>
      } />
      <Route path="/assets/itam" element={
        <ProtectedRoute>
          <ItamImport />
        </ProtectedRoute>
      } />
      <Route path="/assets/dependencies" element={
        <ProtectedRoute>
          <AssetDependencies />
        </ProtectedRoute>
      } />
      <Route path="/assets/:id" element={
        <ProtectedRoute>
          <AssetDetail />
        </ProtectedRoute>
      } />
      <Route path="/assets/:id/edit" element={
        <ProtectedRoute>
          <AssetForm />
        </ProtectedRoute>
      } />
      
      {/* Requirements */}
      <Route path="/requirements" element={
        <ProtectedRoute>
          <Requirements />
        </ProtectedRoute>
      } />
      
      {/* Tasks */}
      <Route path="/tasks" element={
        <ProtectedRoute>
          <Tasks />
        </ProtectedRoute>
      } />
      <Route path="/tasks/new" element={
        <ProtectedRoute>
          <Tasks />
        </ProtectedRoute>
      } />
      
      {/* Evidence */}
      <Route path="/evidence" element={
        <ProtectedRoute>
          <Evidence />
        </ProtectedRoute>
      } />
      <Route path="/evidence/new" element={
        <ProtectedRoute>
          <Evidence />
        </ProtectedRoute>
      } />
      
      {/* Incidents */}
      <Route path="/incidents" element={
        <ProtectedRoute>
          <Incidents />
        </ProtectedRoute>
      } />
      
      {/* Risks */}
      <Route path="/risks" element={
        <ProtectedRoute>
          <Risks />
        </ProtectedRoute>
      } />
      
      {/* Vulnerabilities */}
      <Route path="/vulnerabilities" element={
        <ProtectedRoute>
          <Vulnerabilities />
        </ProtectedRoute>
      } />
      
      {/* Reports */}
      <Route path="/reports" element={
        <ProtectedRoute>
          <Reports />
        </ProtectedRoute>
      } />
      
      {/* Notifications */}
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Notifications />
        </ProtectedRoute>
      } />
      
      {/* Approvals */}
      <Route path="/approvals" element={
        <ProtectedRoute>
          <Approvals />
        </ProtectedRoute>
      } />
      
      {/* Settings */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/settings/users" element={
        <ProtectedRoute>
          <UsersManagement />
        </ProtectedRoute>
      } />
      <Route path="/settings/leads" element={
        <ProtectedRoute requireRole="admin">
          <LeadsDashboard />
        </ProtectedRoute>
      } />
      <Route path="/settings/insights" element={
        <ProtectedRoute requireRole="admin">
          <InsightsAdmin />
        </ProtectedRoute>
      } />
      <Route path="/settings/growth" element={
        <ProtectedRoute requireRole="admin">
          <GrowthDashboard />
        </ProtectedRoute>
      } />

      {/* EU AI Act */}
      <Route path="/ai-act/dashboard" element={
        <ProtectedRoute><AiActDashboard /></ProtectedRoute>
      } />
      <Route path="/ai-act/systems" element={
        <ProtectedRoute><AiSystemsList /></ProtectedRoute>
      } />
      <Route path="/ai-act/systems/:id" element={
        <ProtectedRoute><AiSystemDetail /></ProtectedRoute>
      } />
      <Route path="/ai-act/systems/:id/classify" element={
        <ProtectedRoute><AiClassifyWizard /></ProtectedRoute>
      } />
      <Route path="/ai-act/risks" element={
        <ProtectedRoute><AiRisks /></ProtectedRoute>
      } />
      <Route path="/ai-act/incidents" element={
        <ProtectedRoute><AiIncidents /></ProtectedRoute>
      } />
      <Route path="/ai-act/gpai" element={
        <ProtectedRoute><GpaiModels /></ProtectedRoute>
      } />
      <Route path="/ai-act/audit" element={
        <ProtectedRoute><AiAuditTrail /></ProtectedRoute>
      } />
      <Route path="/ai-act/post-market" element={
        <ProtectedRoute><AiPostMarket /></ProtectedRoute>
      } />
      <Route path="/ai-act/requirements" element={
        <ProtectedRoute><AiActRequirements /></ProtectedRoute>
      } />
      <Route path="/compliance/calendar" element={
        <ProtectedRoute><ComplianceCalendar /></ProtectedRoute>
      } />
      <Route path="/board" element={
        <ProtectedRoute><BoardGovernance /></ProtectedRoute>
      } />
      <Route path="/training" element={
        <ProtectedRoute><Training /></ProtectedRoute>
      } />
      <Route path="/suppliers" element={
        <ProtectedRoute><Suppliers /></ProtectedRoute>
      } />
      <Route path="/policies" element={
        <ProtectedRoute><Policies /></ProtectedRoute>
      } />
      <Route path="/bcm" element={
        <ProtectedRoute><Bcm /></ProtectedRoute>
      } />
      <Route path="/audits" element={
        <ProtectedRoute><Audits /></ProtectedRoute>
      } />
      <Route path="/dpo" element={
        <ProtectedRoute><Dpo /></ProtectedRoute>
      } />
      <Route path="/documents" element={
        <ProtectedRoute><Documents /></ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ComplianceModeProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </ComplianceModeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
